import { useState, useRef, useEffect } from 'react'
import type { Notification } from './useNotifications'

interface NotificationBellProps {
  notifications: Notification[]
  unreadCount: number
  loading?: boolean
  onMarkRead: (id: string) => void
  onMarkAllRead: () => void
  onViewAll?: () => void
  placement?: 'bottom-right' | 'right'
  tooltipLabel?: string
  itemHoverClass?: string
  /** The page this bell leads to is the current page. Strongest highlight. */
  isActive?: boolean
  activeClass?: string
  /** The popover is open but the page is elsewhere. A softer highlight so the bell and Messages never look equally active. */
  openClass?: string
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime()
  const minutes = Math.floor(diff / 60000)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days === 1) return 'Yesterday'
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

export function NotificationBell({
  notifications,
  unreadCount,
  loading,
  onMarkRead,
  onMarkAllRead,
  onViewAll,
  placement = 'bottom-right',
  tooltipLabel,
  itemHoverClass = 'hover:bg-slate-100 dark:hover:bg-slate-800/60',
  isActive = false,
  activeClass = 'bg-slate-200/80 dark:bg-slate-700 text-slate-800 dark:text-slate-100',
  openClass,
}: NotificationBellProps) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('mousedown', handleClick)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [])

  const displayCount = unreadCount > 9 ? '9+' : String(unreadCount)
  const recent = notifications.slice(0, 6)

  const flyoutClass = placement === 'right'
    ? 'absolute left-full bottom-0 ml-3 w-96 rounded-lg border bg-popover/80 backdrop-blur-md text-popover-foreground shadow-lg z-50'
    : 'absolute right-0 mt-2 w-96 rounded-lg border bg-popover/80 backdrop-blur-md text-popover-foreground shadow-lg z-50'

  return (
    <div ref={ref} className="group relative">
      {/* Bell button */}
      <button
        type="button"
        aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
        onClick={() => setOpen(o => !o)}
        className={[
          'relative p-2 rounded-md transition-colors',
          isActive
            ? activeClass
            : open
              ? (openClass ?? activeClass)
              : 'text-slate-400 dark:text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800/60',
        ].join(' ')}
      >
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
          />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 min-w-[16px] h-4 px-0.5 flex items-center justify-center rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold leading-none animate-pulse">
            {displayCount}
          </span>
        )}
      </button>

      {/* Hover tooltip — only when flyout is closed */}
      {!open && tooltipLabel && (
        <span
          aria-hidden
          className="
            absolute start-full ms-3 top-1/2 -translate-y-1/2
            px-3 py-2 rounded-lg z-50
            bg-slate-800 dark:bg-sand-100
            opacity-0 group-hover:opacity-100
            pointer-events-none transition-opacity duration-150 shadow-sm
            text-sm font-medium text-white dark:text-slate-800 font-sans leading-tight whitespace-nowrap
          "
        >
          {tooltipLabel}
        </span>
      )}

      {/* Flyout */}
      {open && (
        <div className={flyoutClass}>
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <h3 className="font-semibold text-sm">Notifications</h3>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={onMarkAllRead}
                className="text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                Mark all read
              </button>
            )}
          </div>

          {/* Notification list */}
          <div className="max-h-96 overflow-y-auto">
            {loading && recent.length === 0 ? (
              <div className="px-4 py-8 text-center text-muted-foreground text-sm">Loading…</div>
            ) : recent.length === 0 ? (
              <div className="px-4 py-8 text-center text-muted-foreground text-sm">
                You're all caught up
              </div>
            ) : (
              recent.map(n => (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => {
                    onMarkRead(n.id)
                    setOpen(false)
                  }}
                  className={[
                    'w-full text-left px-4 py-3 flex gap-3 transition-colors',
                    itemHoverClass,
                    !n.readAt ? 'border-l-2 border-ocean-500 bg-accent/40' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{n.subject}</p>
                    <p className="text-xs text-muted-foreground line-clamp-2">{n.body}</p>
                  </div>
                  <span className="text-xs text-muted-foreground whitespace-nowrap flex-shrink-0 mt-0.5">
                    {timeAgo(n.createdAt)}
                  </span>
                </button>
              ))
            )}
          </div>

          {/* Footer */}
          <div className="border-t px-4 py-3">
            <button
              type="button"
              onClick={() => { setOpen(false); onViewAll?.() }}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              View all notifications →
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
